export type ImportFileEncoding = 'utf-8' | 'utf-16' | 'windows-1252';

export type DecodedImportFile = {
  text: string;
  encoding: ImportFileEncoding;
};

/**
 * Thrown for bytes no single decoding reads safely: UTF-8 encoded characters
 * next to invalid sequences.
 */
export class ImportFileEncodingError extends Error {
  constructor() {
    super('The file mixes UTF-8 with another encoding');
    this.name = 'ImportFileEncodingError';
  }
}

function startsWith(bytes: Uint8Array, prefix: readonly number[]): boolean {
  return prefix.every((byte, index) => bytes[index] === byte);
}

function isContinuationByte(byte: number | undefined): boolean {
  return byte !== undefined && byte >= 0x80 && byte <= 0xbf;
}

/**
 * Latin-1 letters take two UTF-8 bytes. Of the three-byte characters only
 * typography, currency and letterlike symbols (U+2000..U+21FF, `E2 80..87`)
 * count, because Windows-1252 text forms other three-byte sequences by
 * accident (`é§§` is `E9 A7 A7`).
 */
function holdsUtf8Character(bytes: Uint8Array): boolean {
  return bytes.some((byte, index) => {
    const next = bytes[index + 1];
    if (byte === 0xc2 || byte === 0xc3) {
      return isContinuationByte(next);
    }
    return (
      byte === 0xe2 &&
      next !== undefined &&
      next >= 0x80 &&
      next <= 0x87 &&
      isContinuationByte(bytes[index + 2])
    );
  });
}

function decodeStrictUtf8(bytes: Uint8Array): string | undefined {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return undefined;
  }
}

function decodeUtf8OrWindows1252(bytes: Uint8Array): DecodedImportFile {
  const utf8 = decodeStrictUtf8(bytes);
  if (utf8 !== undefined) {
    return { text: utf8, encoding: 'utf-8' };
  }

  if (holdsUtf8Character(bytes)) {
    throw new ImportFileEncodingError();
  }
  return {
    text: new TextDecoder('windows-1252').decode(bytes),
    encoding: 'windows-1252',
  };
}

/**
 * Decodes an uploaded text file: a UTF-16 BOM selects UTF-16, otherwise strict
 * UTF-8, otherwise Windows-1252 as written by Excel on Windows. A UTF-8 BOM
 * never reaches the returned text.
 *
 * @throws {ImportFileEncodingError} when the bytes fail UTF-8 yet hold UTF-8 encoded characters.
 */
export async function decodeImportFile(
  file: File | ArrayBuffer,
): Promise<DecodedImportFile> {
  const bytes = new Uint8Array(
    file instanceof ArrayBuffer ? file : await file.arrayBuffer(),
  );

  if (startsWith(bytes, [0xff, 0xfe])) {
    return {
      text: new TextDecoder('utf-16le').decode(bytes),
      encoding: 'utf-16',
    };
  }
  if (startsWith(bytes, [0xfe, 0xff])) {
    return {
      text: new TextDecoder('utf-16be').decode(bytes),
      encoding: 'utf-16',
    };
  }

  const payload = startsWith(bytes, [0xef, 0xbb, 0xbf])
    ? bytes.subarray(3)
    : bytes;
  return decodeUtf8OrWindows1252(payload);
}
